import mixpanel from 'mixpanel-browser';
import { devLogger } from './dev-logger';

// Mixpanel configuration - get token from Supabase secrets
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const isDevelopment = import.meta.env.NODE_ENV === 'development';

// Initialize Mixpanel
if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: isDevelopment,
    track_pageview: true,
    persistence: 'localStorage',
    ignore_dnt: false,
    api_host: 'https://api.mixpanel.com',
    loaded: function(mixpanel) {
      devLogger.log('🔍 Mixpanel loaded successfully with token:', MIXPANEL_TOKEN.substring(0, 8) + '...');
    }
  });
  devLogger.log('🚀 Mixpanel initialized successfully');
} else {
  devLogger.warn('⚠️ Mixpanel token not found. Analytics will be disabled.');
}

// Analytics service
export class Analytics {
  private static isEnabled = !!MIXPANEL_TOKEN;

  // User identification
  static identify(userId: string, userProperties?: Record<string, any>) {
    if (!this.isEnabled) return;
    
    mixpanel.identify(userId);
    if (userProperties) {
      mixpanel.people.set(userProperties);
    }
    devLogger.log('👤 User identified:', userId);
  }

  // Set user properties
  static setUserProperties(properties: Record<string, any>) {
    if (!this.isEnabled) return;
    
    mixpanel.people.set(properties);
    devLogger.log('📝 User properties updated:', properties);
  }

  // Track events
  static track(eventName: string, properties?: Record<string, any>) {
    if (!this.isEnabled) return;
    
    const eventProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_title: document.title,
    };

    mixpanel.track(eventName, eventProperties);
    devLogger.log('📊 Event tracked:', eventName, eventProperties);
  }

  // Page view tracking
  static trackPageView(pageName: string, properties?: Record<string, any>) {
    this.track('Page Viewed', {
      page_name: pageName,
      ...properties
    });
  }

  // Authentication events
  static trackSignUp(method: 'email' | 'google', properties?: Record<string, any>) {
    this.track('User Signed Up', {
      signup_method: method,
      ...properties
    });
  }

  static trackSignIn(method: 'email' | 'google', properties?: Record<string, any>) {
    this.track('User Signed In', {
      signin_method: method,
      ...properties
    });
  }

  static trackSignOut() {
    this.track('User Signed Out');
  }

  // Document events
  static trackDocumentUploaded(documentInfo: {
    file_name: string;
    file_size: number;
    file_type: string;
  }) {
    this.track('Document Uploaded', {
      document_name: documentInfo.file_name,
      document_size_mb: Math.round(documentInfo.file_size / (1024 * 1024) * 100) / 100,
      document_type: documentInfo.file_type
    });
  }

  static trackDocumentViewed(documentId: string, documentName: string) {
    this.track('Document Viewed', {
      document_id: documentId,
      document_name: documentName
    });
  }

  static trackDocumentDeleted(documentId: string, documentName: string) {
    this.track('Document Deleted', {
      document_id: documentId,
      document_name: documentName
    });
  }

  // Session events
  static trackSessionStarted(sessionInfo: {
    session_id: string;
    document_id: string;
    document_name: string;
  }) {
    this.track('Study Session Started', {
      session_id: sessionInfo.session_id,
      document_id: sessionInfo.document_id,
      document_name: sessionInfo.document_name
    });
  }

  static trackSessionEnded(sessionInfo: {
    session_id: string;
    duration_seconds: number;
    messages_count: number;
  }) {
    this.track('Study Session Ended', {
      session_id: sessionInfo.session_id,
      session_duration_minutes: Math.round(sessionInfo.duration_seconds / 60 * 100) / 100,
      messages_exchanged: sessionInfo.messages_count
    });
  }

  // AI interaction events
  static trackAIConversationStarted(sessionId: string) {
    this.track('AI Conversation Started', {
      session_id: sessionId
    });
  }

  static trackAIMessageSent(sessionId: string, messageLength: number) {
    this.track('AI Message Sent', {
      session_id: sessionId,
      message_length: messageLength
    });
  }

  static trackAIMessageReceived(sessionId: string, messageLength: number, responseTime: number) {
    this.track('AI Message Received', {
      session_id: sessionId,
      message_length: messageLength,
      response_time_ms: responseTime
    });
  }

  static trackVoiceInteractionStarted(sessionId: string) {
    this.track('Voice Interaction Started', {
      session_id: sessionId
    });
  }

  static trackVoiceInteractionEnded(sessionId: string, duration: number) {
    this.track('Voice Interaction Ended', {
      session_id: sessionId,
      duration_seconds: duration
    });
  }

  // PDF interaction events
  static trackPDFPageChanged(sessionId: string, pageNumber: number, totalPages: number) {
    this.track('PDF Page Changed', {
      session_id: sessionId,
      page_number: pageNumber,
      total_pages: totalPages,
      progress_percentage: Math.round((pageNumber / totalPages) * 100)
    });
  }

  static trackPDFZoomChanged(sessionId: string, zoomLevel: number) {
    this.track('PDF Zoom Changed', {
      session_id: sessionId,
      zoom_level: zoomLevel
    });
  }

  // Feature usage events
  static trackFeatureUsed(featureName: string, context?: Record<string, any>) {
    this.track('Feature Used', {
      feature_name: featureName,
      ...context
    });
  }

  // Error tracking
  static trackError(errorType: string, errorMessage: string, context?: Record<string, any>) {
    this.track('Error Occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...context
    });
  }

  // Personalization events
  static trackPersonalizationCompleted(preferences: {
    age?: number;
    goals?: string;
    interaction_preferences?: string;
  }) {
    this.track('Personalization Completed', {
      has_age: !!preferences.age,
      has_goals: !!preferences.goals,
      has_interaction_preferences: !!preferences.interaction_preferences
    });
  }

  // Performance events
  static trackPerformanceMetric(metricName: string, value: number, unit: string) {
    this.track('Performance Metric', {
      metric_name: metricName,
      metric_value: value,
      metric_unit: unit
    });
  }

  // Reset for logout
  static reset() {
    if (!this.isEnabled) return;
    
    mixpanel.reset();
    devLogger.log('🔄 Mixpanel reset (user logged out)');
  }
}

export default Analytics;
